import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { CheckCircle2, ChevronLeft, Clock3, Gift, History, Tag, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const redemptionKey = () => (
  globalThis.crypto?.randomUUID?.()
  || `promo-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
);

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const rewardLabel = (item) => {
  if (item?.promo_type === 'deposit_bonus') {
    return `${Number(item?.bonus_percent || 0).toLocaleString()}% next-deposit bonus`;
  }
  if (item?.promo_type === 'wallet') {
    return `$${Number(item?.amount_credited || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} wallet balance credited`;
  }
  return `${Number(item?.amount_credited || 0).toLocaleString()} FP credited`;
};

const statusMeta = {
  credited: { label: 'Reward credited', Icon: CheckCircle2, color: '#6EE7B7', bg: 'rgba(16,185,129,0.12)' },
  used: { label: 'Used on deposit', Icon: CheckCircle2, color: '#67E8F9', bg: 'rgba(6,182,212,0.12)' },
  waiting_for_deposit: { label: 'Waiting for deposit', Icon: Clock3, color: '#FCD34D', bg: 'rgba(245,158,11,0.12)' },
  expired: { label: 'Expired unused', Icon: XCircle, color: '#FCA5A5', bg: 'rgba(239,68,68,0.12)' },
};

export default function PromoCodePage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const [code, setCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [success, setSuccess] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState('');
  const explorePath = tenantSlug ? `/${tenantSlug}/app/explore` : '/app/explore';

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const response = await api.get('/api/promo/history', { params: { limit: 50 } });
      setHistory(Array.isArray(response?.data?.history) ? response.data.history : []);
    } catch (error) {
      setHistoryError(error?.response?.data?.detail || 'Promo history could not be loaded');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const redeem = async () => {
    const cleanCode = code.trim().toUpperCase();
    if (!cleanCode) {
      toast.error('Enter your promo code first');
      return;
    }
    setRedeeming(true);
    try {
      const response = await api.post('/api/promo/redeem', { code: cleanCode }, {
        headers: { 'X-Idempotency-Key': redemptionKey() },
      });
      const data = response.data || {};
      setSuccess(data);
      toast.success(data.benefit || 'Promo redeemed successfully');
      setCode('');
      loadHistory();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'This promo code could not be redeemed');
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div className="player-app min-h-screen pb-12 text-white" data-testid="promo-code-page">
      <div className="flex items-center gap-3 bg-black px-4 py-4 sm:px-6">
        <button
          onClick={() => navigate(explorePath)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 transition active:scale-95"
          aria-label="Back to Explore & Earn"
          data-testid="promo-code-back"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="display flex items-center gap-2 text-xl font-extrabold sm:text-2xl"><Tag className="h-6 w-6 text-[#FFD93D]" /> Redeem Promo Code</h1>
          <p className="text-xs text-white/65">Enter a code from your group to claim its reward.</p>
        </div>
      </div>

      <main className="mx-auto max-w-xl px-4 py-8 sm:px-6">
        <section className="rounded-3xl border border-[#FFD93D]/30 bg-[#101722] p-5 shadow-[0_18px_55px_rgba(0,0,0,0.24)] sm:p-6">
          <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-300 to-orange-500 text-[#241600] shadow-lg shadow-amber-500/20">
            <Gift className="h-7 w-7" strokeWidth={2.6} />
          </div>
          <h2 className="display text-lg font-extrabold">Have a promo code?</h2>
          <p className="mt-1 text-sm leading-6 text-white/65">Promo rewards are credited after the code is checked. Each code can only be used once by an eligible player.</p>

          <label className="mt-5 block text-[11px] font-black uppercase tracking-[0.16em] text-white/50" htmlFor="promo-code-input">Promo code</label>
          <Input
            id="promo-code-input"
            value={code}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
            onKeyDown={(event) => event.key === 'Enter' && redeem()}
            placeholder="ENTER CODE"
            autoCapitalize="characters"
            className="mt-2 h-12 border-white/15 bg-black/25 font-mono font-bold tracking-[0.12em] text-white placeholder:text-white/30"
            data-testid="promo-code-input"
          />
          <Button
            onClick={redeem}
            disabled={redeeming || !code.trim()}
            className="mt-3 h-12 w-full bg-[#FFD93D] font-extrabold text-black hover:bg-[#f7c929]"
            data-testid="promo-code-redeem"
          >
            {redeeming ? 'Redeeming…' : 'Redeem reward'}
          </Button>
        </section>

        {success && (
          <section className="mt-4 rounded-2xl border border-emerald-400/35 bg-emerald-500/10 p-4" data-testid="promo-code-success">
            <p className="font-bold text-emerald-300">Promo redeemed</p>
            <p className="mt-1 text-sm text-white/75">{success.benefit || success.message}</p>
          </section>
        )}

        <section className="mt-6" data-testid="promo-code-history">
          <div className="mb-3 flex items-end justify-between gap-3">
            <div>
              <h2 className="display flex items-center gap-2 text-lg font-extrabold">
                <History className="h-5 w-5 text-[#FFD93D]" /> Promo history
              </h2>
              <p className="mt-1 text-xs text-white/55">Check which codes you redeemed and whether the reward was used.</p>
            </div>
            {!historyLoading && (
              <button onClick={loadHistory} className="text-xs font-bold text-[#FFD93D]" type="button">Refresh</button>
            )}
          </div>

          {historyLoading ? (
            <div className="rounded-2xl border border-white/10 bg-[#101722] p-5 text-center text-sm text-white/55">Loading promo history…</div>
          ) : historyError ? (
            <div className="rounded-2xl border border-red-400/25 bg-red-500/10 p-4">
              <p className="text-sm text-red-200">{historyError}</p>
              <button onClick={loadHistory} className="mt-2 text-xs font-bold text-[#FFD93D]" type="button">Try again</button>
            </div>
          ) : history.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/15 bg-[#101722]/80 p-6 text-center">
              <History className="mx-auto h-7 w-7 text-white/25" />
              <p className="mt-2 text-sm font-bold text-white/70">No promo codes redeemed yet</p>
              <p className="mt-1 text-xs text-white/45">Codes you use will appear here.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((item) => {
                const meta = statusMeta[item.status] || statusMeta.credited;
                const StatusIcon = meta.Icon;
                return (
                  <article key={item.id || `${item.code}-${item.redeemed_at}`} className="rounded-2xl border border-white/10 bg-[#101722] p-4 shadow-lg shadow-black/10">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-mono text-sm font-black tracking-[0.08em] text-[#FFD93D]">{item.code || 'PROMO'}</p>
                        <p className="mt-1 truncate text-sm font-bold text-white">{item.title || 'Promo reward'}</p>
                      </div>
                      <span className="flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wide" style={{ color: meta.color, background: meta.bg }}>
                        <StatusIcon className="h-3.5 w-3.5" /> {meta.label}
                      </span>
                    </div>
                    <div className="mt-3 rounded-xl bg-black/20 px-3 py-2.5">
                      <p className="text-sm font-bold text-white/85">{rewardLabel(item)}</p>
                      <p className="mt-1 text-[11px] text-white/45">Redeemed {formatDate(item.redeemed_at)}</p>
                      {item.used_at && <p className="mt-0.5 text-[11px] text-cyan-200/70">Used {formatDate(item.used_at)}</p>}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
